import Image from 'next/image';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button';
import { getImageBasePath } from 'utils/generics';
import { NewPlanStages, policyDetailsInitState } from '../constants';
import {
    SegmentEventPages,
    SegmentEventPageTypes,
    SegmentEventNameTypes,
    SegmentEventActionNames,
    SegmentEventActionTypes,
    SegmentEventButtonNames,
} from 'utils/segment/constants';
import { MPosContext } from 'utils/context';
import SnackBar from 'library/snackbar';
import * as Styles from './styles';
import sendSegmentEvent from 'utils/segment';
import { useResponsiveDevice } from 'hooks/useResponsiveDevice';

function CustomerLink() {
    const router = useRouter();
    const {
        partnerId,
        planDetails,
        setPlanDetails,
        setActiveStage,
        userDetails,
    } = useContext(MPosContext);
    const { isMobile } = useResponsiveDevice();

    const { customer } = planDetails;

    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });
    const [isBtnLoading, setIsBtnLoading] = useState(false);

    const [linkCopied, setLinkCopied] = useState(false);

    useEffect(() => {
        let timer;
        if (linkCopied) {
            timer = setTimeout(() => {
                setLinkCopied(false);
            }, 1200);
        }

        return () => {
            timer && clearTimeout(timer);
        };
    }, [linkCopied]);

    /**
     * view segment hook
     */
    useEffect(() => {
        sendSegmentEvent(
            SegmentEventNameTypes.VIEW,
            {
                page_type: SegmentEventPageTypes.SEND_LINK_TO_CUSTOMER_PAGE,
                view_name: SegmentEventPages.VIEW_SEND_LINK_TO_CUSTOMER_PAGE,
            },
            {
                userDetails,
                isMobile,
            }
        );
    }, []);

    const linkText = () => {
        if (window !== undefined) {
            let domain = 'https://www.acko.com';
            if (
                window.location.origin.includes('pos-dev') ||
                window.location.origin.includes('pos-auto-dev')
            ) {
                domain = 'https://sandbox2.acko.com';
            } else if (window.location.origin.includes('pos.ackodev.com')) {
                domain = 'https://www.ackodev.com';
            }
            return `${domain}/mpos/partner/${partnerId}?order_ekey=${planDetails.order_ekey}&phone=${customer.phone}`;
        }
        return `https://www.acko.com/mpos/partner/${partnerId}?order_ekey=${planDetails.order_ekey}&phone=${customer.phone}`;
    };

    const copyLink = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    const sendLink = () => {
        import('./apis').then(async (req) => {
            setIsBtnLoading(true);
            const { status, statusCode } = await req.sendLinkToCustomer(
                planDetails.order_ekey,
                partnerId
            );
            setIsBtnLoading(false);
            if (statusCode === 401) {
                router.push('/login');
                setSnackbar({
                    type: 'failure',
                    message: 'Token expired. Redirecting to login page.',
                });
                return;
            }
            if (status) {
                setActiveStage(NewPlanStages.CONGRATULATION);
                setPlanDetails(policyDetailsInitState);
                return;
            }
            setSnackbar({
                type: 'failure',
                message: 'Oops. Something went wrong',
            });
        });

        /**
         * Segment event
         */
        sendSegmentEvent(
            SegmentEventNameTypes.ACTION,
            {
                action_type: SegmentEventActionTypes.TAP_BTN,
                action_name: SegmentEventActionNames.SEND_LINK_TO_CUSTOMER,
                button_name: SegmentEventButtonNames.SEND_TO_CUSTOMER,
            },
            { userDetails, isMobile }
        );
    };

    return (
        <Styles.Wrapper>
            <Styles.ShareLinkImage>
                <Image
                    width={'120px'}
                    height={'120px'}
                    alt={''}
                    src={getImageBasePath('/images/shareEmail.svg')}
                />
            </Styles.ShareLinkImage>
            <Styles.Text>Send link to the customer mobile</Styles.Text>
            <Styles.PhoneNumberWrapper>
                <Styles.PhoneNumber>{customer.phone}</Styles.PhoneNumber>
                <Styles.EditIcon>
                    <Image
                        width={'20px'}
                        height={'20px'}
                        alt={''}
                        src={getImageBasePath('/icons/blueEdit.svg')}
                        onClick={() => setActiveStage(NewPlanStages.IMEI)}
                    />
                </Styles.EditIcon>
            </Styles.PhoneNumberWrapper>
            <Styles.LinkWrapper>
                <Styles.Link>{linkText()}</Styles.Link>
                <span
                    style={{
                        marginLeft: '1rem',
                        marginTop: '4px',
                    }}
                >
                    <Image
                        width={'12px'}
                        height={'12px'}
                        alt={''}
                        src={getImageBasePath('/icons/copy.svg')}
                        onClick={() => {
                            copyLink(linkText());
                            setLinkCopied(true);
                        }}
                    />
                </span>
                {linkCopied && <Styles.Copied>Link copied</Styles.Copied>}
            </Styles.LinkWrapper>
            <Styles.SendButtonWrapper>
                <PrimaryButton loading={isBtnLoading} onClick={sendLink}>
                    Send to customer
                </PrimaryButton>
            </Styles.SendButtonWrapper>
            <SnackBar
                {...snackbar}
                handleSnakbarComplete={() =>
                    setSnackbar({ type: '', message: '' })
                }
            />
        </Styles.Wrapper>
    );
}

export default CustomerLink;
